import React, { useState } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App';
import { pagepilot, getAppKey, setAppKey, validateKey } from './api/client';
import './styles.css';

// Install the HTTP-backed bridge under the same global the Electron preload
// used, so the ported screens keep calling `window.pagepilot.*` unchanged.
window.pagepilot = pagepilot;

// Single-user login: the app key is validated against the API, then stored
// locally and sent on every request. The API rejects everything without it.
function Login({ onAuthed }: { onAuthed: () => void }) {
  const [key, setKey] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    if (await validateKey(key.trim())) {
      setAppKey(key.trim());
      onAuthed();
    } else {
      setErr('Incorrect key.');
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <form onSubmit={submit} className="w-full max-w-sm space-y-4 rounded-xl border border-neutral-800 bg-neutral-900/70 p-6">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-500 text-sm font-bold text-white">▶</div>
          <span className="text-base font-semibold">PagePilot AI</span>
        </div>
        <p className="text-xs text-neutral-400">Enter your access key to continue.</p>
        <input
          type="password"
          autoFocus
          value={key}
          onChange={(e) => setKey(e.target.value)}
          placeholder="Access key"
          className="w-full rounded-lg border border-neutral-700 bg-neutral-900 px-3 py-2 text-sm text-neutral-100 outline-none focus:border-brand-400"
        />
        {err && <p className="text-xs text-red-400">{err}</p>}
        <button
          type="submit"
          disabled={busy || key.trim().length === 0}
          className="w-full rounded-lg bg-brand-500 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-600 disabled:opacity-50"
        >
          {busy ? 'Checking…' : 'Unlock'}
        </button>
      </form>
    </div>
  );
}

function Root() {
  const [authed, setAuthed] = useState(() => getAppKey().length > 0);
  return authed ? <App /> : <Login onAuthed={() => setAuthed(true)} />;
}

createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Root />
  </React.StrictMode>,
);
